// Centralized utilities for workflow session data operations - handles normalization, validation, and restoration. OPTIMIZED: Added caching to reduce redundant computations

import { normalizeToArray } from './dataHelpers';

// OPTIMIZED: Cache for normalized data to avoid redundant computations
const normalizationCache = new WeakMap();

// Normalizes data to array format, filtering out invalid entries - OPTIMIZED: Uses WeakMap cache for object references (data: any) -> Array
export const normalizeWorkflowData = (data) => {
  if (!data) return [];
  
  // OPTIMIZED: Check cache for object references (WeakMap allows garbage collection)
  if (typeof data === 'object' && !Array.isArray(data)) {
    const cached = normalizationCache.get(data);
    if (cached) return cached;
  }
  
  const normalized = normalizeToArray(data);
  const filtered = normalized.filter(d => 
    d !== null && 
    d !== undefined && 
    typeof d === 'object' && 
    Object.keys(d).length > 0
  );
  
  // OPTIMIZED: Cache result for object references
  if (typeof data === 'object' && !Array.isArray(data)) {
    normalizationCache.set(data, filtered);
  }
  
  return filtered;
};

// Checks if workflow session has valid extracted/editable data - OPTIMIZED: Early return for better performance (sessionState: Object) -> boolean
export const hasWorkflowData = (sessionState) => {
  if (!sessionState) return false;
  
  // Check editableData first (usually more up-to-date), early return if found
  const editableData = normalizeWorkflowData(sessionState.editableData);
  if (editableData.length > 0) return true;
  
  // Only check extractedData if editableData is empty
  const extractedData = normalizeWorkflowData(sessionState.extractedData);
  return extractedData.length > 0;
};

// Gets the best available data from workflow session (editableData takes precedence) - (sessionState: Object) -> Array
export const getWorkflowDeclarations = (sessionState) => {
  if (!sessionState) return [];
  
  const editableData = normalizeWorkflowData(sessionState.editableData);
  if (editableData.length > 0) return editableData;
  
  return normalizeWorkflowData(sessionState.extractedData);
};

// Checks if data should auto-display (has data but stepsCompleted might be false) - (sessionState: Object) -> boolean
export const shouldAutoDisplayData = (sessionState) => {
  const hasData = hasWorkflowData(sessionState);
  const stepsCompleted = sessionState?.stepsCompleted ?? false;
  
  // Auto-display if data exists (regardless of stepsCompleted flag)
  return hasData;
};

// Restores selections from workflow session state to global Redux - (sessionState: Object, dispatch: Function)
export const restoreWorkflowSelections = (sessionState, dispatch) => {
  if (!sessionState) {
    // CRITICAL: If no session state, clear all selections to prevent stale data
    dispatch({ type: 'RESET_SELECTED_SENDER' });
    dispatch({ type: 'RESET_SELECTED_RECEIVER' });
    dispatch({ type: 'RESET_SELECTED_AGENT' });
    dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: null });
    return;
  }
  
  // CRITICAL: Clear all selections first, then restore only what exists in workflow session (ensures selections from previous workflow don't persist)
  dispatch({ type: 'RESET_SELECTED_SENDER' });
  dispatch({ type: 'RESET_SELECTED_RECEIVER' });
  dispatch({ type: 'RESET_SELECTED_AGENT' });
  
  // Restore selectedSender from workflow session - only if array has actual objects
  if (sessionState.selectedSender && Array.isArray(sessionState.selectedSender) && sessionState.selectedSender.length > 0) {
    sessionState.selectedSender.forEach((sender, index) => {
      if (sender && typeof sender === 'object') {
        dispatch({ type: 'SET_SELECTED_SENDER', payload: { index, sender } });
      }
    });
  }
  // CRITICAL: If workflow has empty array or no selections, ensure global Redux is cleared (prevents components from falling back to stale global state values)
  else if (!sessionState.selectedSender || (Array.isArray(sessionState.selectedSender) && sessionState.selectedSender.length === 0)) {
    dispatch({ type: 'RESET_SELECTED_SENDER' });
  }
  
  // Restore selectedReceiver from workflow session - only if array has actual objects
  if (sessionState.selectedReceiver && Array.isArray(sessionState.selectedReceiver) && sessionState.selectedReceiver.length > 0) {
    sessionState.selectedReceiver.forEach((receiver, index) => {
      if (receiver && typeof receiver === 'object') {
        dispatch({ type: 'SET_SELECTED_RECEIVER', payload: { index, receiver } });
      }
    });
  }
  // CRITICAL: If workflow has empty array or no selections, ensure global Redux is cleared
  else if (!sessionState.selectedReceiver || (Array.isArray(sessionState.selectedReceiver) && sessionState.selectedReceiver.length === 0)) {
    dispatch({ type: 'RESET_SELECTED_RECEIVER' });
  }
  
  // Restore selectedAgent from workflow session - only if array has actual objects
  if (sessionState.selectedAgent && Array.isArray(sessionState.selectedAgent) && sessionState.selectedAgent.length > 0) {
    sessionState.selectedAgent.forEach((agent, index) => {
      if (agent && typeof agent === 'object') {
        dispatch({ type: 'SET_SELECTED_AGENT', payload: { index, agent } });
      }
    });
  }
  // CRITICAL: If workflow has empty array or no selections, ensure global Redux is cleared
  else if (!sessionState.selectedAgent || (Array.isArray(sessionState.selectedAgent) && sessionState.selectedAgent.length === 0)) {
    dispatch({ type: 'RESET_SELECTED_AGENT' });
  }
  
  // CRITICAL: Restore selectedTemplate (for template colors/themes) - stored in workflow session state, but also set in global state for backward compatibility (source of truth is workflow session state)
  if (sessionState.selectedTemplate) {
    dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: sessionState.selectedTemplate });
  } else {
    // Clear template if workflow doesn't have one
    dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: null });
  }
};

// Determines if workflow session should be auto-restored on mount - (sessionState: Object, hasExistingData: boolean, hasTabData: boolean) -> boolean
export const shouldAutoRestoreWorkflow = (sessionState, hasExistingData, hasTabData) => {
  if (!sessionState) return false;
  
  // Don't auto-restore if Redux already has data
  if (hasExistingData) return false;
  
  // Auto-restore if workflow has data (extractedData or editableData)
  return hasWorkflowData(sessionState) || hasTabData;
};

